import React, {
  FC,
  useRef,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
} from "react";
import Modal from "../Modal";
import { jsPDF } from "jspdf";
import LoadingIcon from "../LodingIcon";
import {
  alertService,
  getMerchantVoucherFile,
  getMerchantVoucherPath,
  printMerchantVoucherFile,
} from "../../services";
import { MerchantVoucherCommand } from "../../services/merchantServices";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import {
  XMarkIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

// Font size of the ticket, in px, before fitting it to the width of the modal
const BASE_FONT_SIZE = 13;
const MIN_FONT_SIZE = 8;

// Messages of the two steps the modal goes through: the vpos generates the
// voucher and then the local service reads the file it wrote
const MERCHANT_TEXT = "Consultando Merchant. Por favor espere...";
const READING_TEXT = "Leyendo voucher. Por favor espere...";

interface MerchantVoucherModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  command?: MerchantVoucherCommand;
  // Full path of a voucher file on the machine that runs the local printer
  // service. Takes precedence over command for both reading and printing
  voucherFile?: string;
  // When set, the modal offers downloading the voucher as a PDF with this name
  pdfName?: string;
  // Whether vouchers are read and printed through the local service.
  // undefined while the vpos version is still unknown; versions below 3.16
  // show and print every voucher from the merchant interface itself, so the
  // local service must not be contacted at all
  localPrintingEnabled: boolean | undefined;
}

export const MerchantVoucherModal: FC<MerchantVoucherModalProps> = ({
  openModal,
  setOpenModal,
  title,
  command,
  voucherFile,
  pdfName,
  localPrintingEnabled,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(READING_TEXT);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const [voucher, setVoucher] = useState("");
  // Path of the file being shown, either the one received as a prop or the one
  // the merchant method answered with. It is what gets printed
  const [voucherPath, setVoucherPath] = useState("");

  const preRef = useRef<HTMLPreElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  // Removes the leading blank lines and the trailing spaces of every line: the
  // padding of the original ticket only adds empty space and horizontal scroll
  const voucherText = useMemo(
    () =>
      voucher
        .replace(/^(?:[ \t]*\r?\n)+/, "")
        .replace(/\s+$/, "")
        .split("\n")
        .map((line) => line.replace(/\s+$/, ""))
        .join("\n"),
    [voucher]
  );

  // Reads the voucher every time the modal is opened, so the content is never stale
  useEffect(() => {
    if (!openModal || (!command && !voucherFile)) return;

    // The local reading service must not be contacted below 3.16, nor before
    // the vpos version is known
    if (localPrintingEnabled !== true) {
      setLoading(false);
      setVoucher("");
      setError(
        "Esta versión del vpos muestra e imprime los vouchers desde la interfaz Merchant."
      );
      return;
    }

    let cancelled = false;

    const getVoucher = async () => {
      setLoading(true);
      setError("");
      setVoucher("");
      setVoucherPath("");

      // A command has no file yet: the merchant method is called first and
      // answers with the path of the voucher it generated. A voucherFile was
      // already resolved that way by the operation that opened the modal
      let filePath = voucherFile;

      if (!filePath) {
        setLoadingText(MERCHANT_TEXT);

        const path = await getMerchantVoucherPath(command!);

        if (cancelled) return;

        if (path.didError || !path.model) {
          setError(
            path.errorMessage || "No se pudo obtener el voucher solicitado."
          );
          setLoading(false);
          return;
        }

        filePath = path.model;
      }

      setLoadingText(READING_TEXT);
      setVoucherPath(filePath);

      const response = await getMerchantVoucherFile(filePath);

      if (cancelled) return;

      if (response.didError || !response.model) {
        setError(
          response.errorMessage || "No se pudo obtener el voucher solicitado."
        );
      } else {
        setVoucher(response.model);
      }

      setLoading(false);
    };

    getVoucher();

    return () => {
      cancelled = true;
    };
  }, [openModal, command, voucherFile, localPrintingEnabled]);

  // Shrinks the text until the widest line fits, so the ticket never scrolls
  // horizontally. Measuring the rendered width keeps it font agnostic.
  useLayoutEffect(() => {
    const pre = preRef.current;
    const paper = paperRef.current;

    if (!pre || !paper || !voucherText) return;

    const fitVoucher = () => {
      const available = paper.clientWidth;

      pre.style.fontSize = `${BASE_FONT_SIZE}px`;
      const needed = pre.scrollWidth;

      if (!available || !needed) return;

      const fontSize =
        needed > available
          ? Math.max(
              MIN_FONT_SIZE,
              Math.floor((BASE_FONT_SIZE * available) / needed)
            )
          : BASE_FONT_SIZE;

      pre.style.fontSize = `${fontSize}px`;
      pre.style.lineHeight = `${Math.round(fontSize * 1.45)}px`;
    };

    fitVoucher();

    // The available width changes when the window is resized
    const observer = new ResizeObserver(fitVoucher);
    observer.observe(paper);

    return () => observer.disconnect();
  }, [voucherText]);

  // Prints the file that is being shown through the local printer service, so
  // the vpos is not asked for the voucher a second time
  const handlePrint = async () => {
    if (!voucherPath || printing || localPrintingEnabled !== true) return;

    setPrinting(true);
    const response = await printMerchantVoucherFile(voucherPath);
    setPrinting(false);

    if (response.didError) {
      alertService.error(
        "No se pudo imprimir el voucher",
        response.errorMessage
      );
      return;
    }

    alertService.success("Voucher enviado a la impresora");
  };

  // Downloads the voucher text as a PDF file, keeping the monospace layout
  const handleDownloadPdf = () => {
    if (!pdfName || !voucherText) return;

    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 40;
    const lineHeight = 12;
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont("courier", "normal");
    doc.setFontSize(10);

    let y = margin;
    for (const line of voucherText.split("\n")) {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }

    doc.save(pdfName);
  };

  return (
    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      className="w-full sm:max-w-xl"
    >
      {/* Header */}
      <div className="flex flex-row justify-between items-start mb-4 gap-8">
        <h2 className="text-xl font-bold">{title}</h2>

        <div className="ml-3 flex h-7 items-center">
          <button
            type="button"
            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => setOpenModal(false)}
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Cerrar</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col items-center justify-center bg-gray-100 rounded-lg p-4 min-h-[16rem]">
        {loading && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <LoadingIcon size="3rem" />
            <p className="text-gray-600 font-medium text-center">
              {loadingText}
            </p>
          </div>
        )}

        {!loading && !!error && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500" />
            <p className="text-gray-600 font-medium text-center">{error}</p>
          </div>
        )}

        {!loading && !error && (
          // Paper sheet: monospace text keeps the alignment of the original ticket
          <div className="w-full bg-white shadow-md border border-gray-200 px-5 py-6 overflow-y-auto overflow-x-hidden max-h-[26rem]">
            <div ref={paperRef} className="w-full">
              <pre
                ref={preRef}
                className="font-mono text-gray-900 whitespace-pre"
                style={{ fontSize: BASE_FONT_SIZE }}
              >
                {voucherText}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-row gap-4 items-center justify-between mt-6">
        <SecondaryButton onClick={() => setOpenModal(false)} className="px-8">
          Cerrar
        </SecondaryButton>

        <div className="flex flex-row gap-4 items-center">
          {!!pdfName && (
            <SecondaryButton
              disabled={loading || !!error || !voucherText}
              onClick={handleDownloadPdf}
              className="px-4 gap-1"
            >
              <ArrowDownTrayIcon
                className="h-5 w-5 flex-none"
                aria-hidden="true"
              />
              Descargar PDF
            </SecondaryButton>
          )}

          {localPrintingEnabled === true && (
            <PrimaryButton
              disabled={loading || printing || !!error || !voucherText}
              onClick={handlePrint}
              className="px-8 gap-1"
            >
              <PrinterIcon className="h-5 w-5 flex-none" aria-hidden="true" />
              {printing ? "Imprimiendo..." : "Imprimir"}
            </PrimaryButton>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MerchantVoucherModal;
